import { useMemo } from "react";
import dayjs from "dayjs";
import { ViewMode } from "@thepocman/gantt-task-react";

import TimelineLibrary from "@/components/Timeline/TimelineLibrary";
import TimelineHeaderControls from "@/components/Timeline/TimelineHeaderControls";
import { useTimelineControls } from "@/components/Timeline/useTimelineControls";
import type { TimelineItem } from "@/components/Timeline/timeline";
import { milestonesTimelineUtils } from "@/components/Timeline/milestonesTimelineUtils";
import {
  getEarliestTimelineDate,
  getLatestTimelineDate,
} from "@/components/Timeline/timelineSortUtils";

const TIMELINE_COLUMNS = [
  { fieldName: "trial", visibility: "visible" as const },
  { fieldName: "event_type", visibility: "visible" as const },
  { fieldName: "event_name", visibility: "visible" as const },
  { fieldName: "start_date", visibility: "visible" as const },
  { fieldName: "end_date", visibility: "visible" as const },
];

export function PortfolioMilestonesTimeline({
  data,
}: {
  data?: TimelineItem[];
}) {
  const rows = useMemo(
    () => (Array.isArray(data) ? data : []).filter((item) => item.start_date),
    [data],
  );

  const firstEventDate = useMemo(() => getEarliestTimelineDate(rows), [rows]);
  const lastEventDate = useMemo(() => getLatestTimelineDate(rows), [rows]);

  const timelineControls = useTimelineControls({
    firstEventDate,
    initialViewMode: ViewMode.Year,
    initializeToFirstEvent: true,
  });

  return (
    <div className="mt-2">
      <TimelineHeaderControls
        viewMode={timelineControls.viewMode}
        selectedDate={timelineControls.selectedDate}
        isTodayActive={timelineControls.isTodayActive}
        isFirstEventActive={timelineControls.isFirstEventActive}
        hasFirstEventDate={Boolean(firstEventDate)}
        minSelectableDate={firstEventDate}
        maxSelectableDate={lastEventDate}
        testIdPrefix="portfolio-timeline"
        onViewModeChange={timelineControls.handleViewModeChange}
        onDateChange={timelineControls.handleDateChange}
        onTodayClick={timelineControls.handleTodayClick}
        onEarliestClick={timelineControls.handleEarliestClick}
      />

      <TimelineLibrary
        data={rows}
        viewMode={timelineControls.viewMode}
        viewDate={timelineControls.viewDate}
        centerOnDate={timelineControls.centerOnDate}
        centerOnDateRequestId={timelineControls.centerOnDateRequestId}
        columns={TIMELINE_COLUMNS}
        utils={milestonesTimelineUtils}
        viewportOffset={220}
        onHorizontalScroll={timelineControls.handleHorizontalScroll}
        onRowSelect={(item) =>
          timelineControls.handleDateChange(dayjs(item.start_date))
        }
      />
    </div>
  );
}
